import React, { Suspense, use, useEffect, useRef, useState } from "react";
import Button from '@mui/material/Button';
import Typography from "@mui/material/Typography";
import Paper from '@mui/material/Paper';
import LinearProgress from '@mui/material/LinearProgress';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import StopIcon from '@mui/icons-material/Stop';
import { APIServices } from "../../../hooks/fetchAPI";
import { useNavigate } from "react-router-dom";
import { pageRoutes } from "../../../routes/pageRoutes";
import { storageApi } from "../../../contant";
import { useSelector } from "react-redux";
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import { BsClock } from "react-icons/bs";
const PreviewCamRec = React.lazy(() => import('../Modal/PreviewCamRec'));
const InterviewInstructions = React.lazy(() => import('./InterviewInstructions'));
const WarningNotif = React.lazy(() => import('../Modal/WarningNotif'));
const MediaErrorModal = React.lazy(() => import('../Modal/MediaErrorModal'));


const MAX_RECORD_TIME = 300; // 5 minutes in seconds

const OnewayInterview = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState("start"); // "start" | "question" | "recording" | "review" | "finished"
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [recording, setRecording] = useState(false);
    const [timer, setTimer] = useState(0);
    const [countdown, setCountdown] = useState(0);
    const mediaRecorderRef = useRef(null);
    const webcamRef = useRef(null);
    const questionVideoRef = useRef(null);
    const chunksRef = useRef([]);
    const [stream, setStream] = useState(null);
    const [recordedBlob, setRecordedBlob] = useState(null);
    const [isQuestionVideoPlaying, setIsQuestionVideoPlaying] = useState(false);
    const [questionVideoProgress, setQuestionVideoProgress] = useState(0);
    const [isQuestionVideoMuted, setIsQuestionVideoMuted] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [mediaError, setMediaError] = React.useState(null);
    const [questions, setQuestions] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [isTabActive, setIsTabActive] = useState(true);
    const [switchCount, setSwitchCount] = useState(0);
    const [openWarning, setOpenWarning] = useState(false);
    const [attemptCount, setAttemptCount] = useState(0);
    const [videoQuality, setVideoQuality] = useState('360');
    const [anchorElQuality, setAnchorElQuality] = useState(null);
    const openQualityMenu = Boolean(anchorElQuality);
    const [isloadingQuestions, setIsLoadingQuestions] = useState(false);
    const [uploadPercent, setUploadPercent] = useState(0);
    const currentQuestion = questions[currentQuestionIndex];
    const totalQuestions = questions.length;
    const user = useSelector((state) => state.user.userData)
    const [timeLeft, setTimeLeft] = useState(60 * 30);
    const [recordedURL, setRecordedUrl] = useState("")


    useEffect(() => {
        if (timeLeft <= 0) {
            navigate(pageRoutes.APPLICANT);
        }

        const timer = setInterval(() => {
            setTimeLeft(prev => Math.max(prev - 1, 0));
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        if (step === "finished") {
            localStorage.setItem('examCompleted', true);
            navigate(pageRoutes.APPLICANT);
        }
    }, [step, navigate]);

    const fetchAPI = async () => {
        setIsLoadingQuestions(true);
        try {
            const response = await APIServices.get({ url: `/video-assessment/user-answers`, payload: { quality: videoQuality } });
            if (response && response.status === 200) {
                setQuestions(response?.data?.questions || []);

            } else {
                console.error("Failed to fetch video assessment questions:", response);
            }
        } catch (error) {
            console.error("Error fetching video assessment questions:", error);
        } finally {
            setIsLoadingQuestions(false);
        }
    }

    const fetchAPI2 = async () => {
        try {
            const response = await APIServices.get({ url: `/video-assessment/questions`, payload: { quality: videoQuality } });
            if (response && response.status === 200) {
                const questionSet = response?.data?.data;

                const data = questionSet?.find(
                    (qs) => qs.id === user?.experience_set
                );

            } else {
                console.error("Failed to fetch video assessment questions:", response);
            }
        } catch (error) {
            console.error("Error fetching video assessment questions:", error);
        }
    }

    useEffect(() => {
        fetchAPI();

    }, [videoQuality]);

    useEffect(() => {
        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);


    useEffect(() => {
        let mediaStream;
        const startPreview = async () => {
            try {
                mediaStream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        width: { ideal: 640, max: 640 },
                        height: { ideal: 480, max: 480 },
                        frameRate: { ideal: 24, max: 24 }
                    },
                    audio: true
                });
                setStream(mediaStream);
                setMediaError(null);
                if (webcamRef.current) {
                    webcamRef.current.srcObject = mediaStream;
                }
            } catch (err) {
                console.error("Error accessing media devices for preview.", err);
                setMediaError("Camera and microphone access is required. Please enable permissions in your browser settings and refresh the page.");
            }
        };

        startPreview();

        return () => {
            if (mediaStream) {
                mediaStream.getTracks().forEach((track) => track.stop());
            }
        };
    }, [videoQuality]);


    useEffect(() => {
        if (questions.length > 0) {
            const firstUnansweredIndex = questions.findIndex(q => q.answer === null);
            if (firstUnansweredIndex !== -1) {
                if (currentQuestionIndex < firstUnansweredIndex) {
                    setCurrentQuestionIndex(firstUnansweredIndex);
                }
            } else {
                setStep("finished");
            }
        }
    }, [questions]);


    useEffect(() => {
        if (webcamRef.current && stream && (step === 'question' || step === 'recording')) {
            webcamRef.current.srcObject = stream;
        }
    }, [stream, step, currentQuestionIndex]);

    const handleVideoPlayPause = () => {
        if (questionVideoRef.current) {
            if (questionVideoRef.current.paused) {
                questionVideoRef.current.play();
                setIsQuestionVideoPlaying(true);
            } else {
                questionVideoRef.current.pause();
                setIsQuestionVideoPlaying(false);
            }
        }
    };

    const handleVolumeToggle = () => {
        if (questionVideoRef.current) {
            questionVideoRef.current.muted = !questionVideoRef.current.muted;
            setIsQuestionVideoMuted(questionVideoRef.current.muted);
        }
    };

    const handleFullscreen = () => {
        if (questionVideoRef.current.requestFullscreen) {
            questionVideoRef.current.requestFullscreen();
        } else if (questionVideoRef.current.webkitRequestFullscreen) { /* Safari */
            questionVideoRef.current.webkitRequestFullscreen();
        } else if (questionVideoRef.current.msRequestFullscreen) { /* IE11 */
            questionVideoRef.current.msRequestFullscreen();
        }
    };

    const handleTimeUpdate = () => {
        if (questionVideoRef.current) {
            let progress = (questionVideoRef.current.currentTime / questionVideoRef.current.duration) * 100;
            if (progress > 90) {
                progress = 98;
            }
            setQuestionVideoProgress(progress)
        }
    }

    const handleQualityClick = (event) => {
        setAnchorElQuality(event.currentTarget);
    };

    const handleQualityClose = () => {
        setAnchorElQuality(null);
    };

    const handleQualitySelect = (quality) => {
        setVideoQuality(quality);
        handleQualityClose();
    };

    // Start recording with countdown on left panel
    const startRecording = () => {
        if (questionVideoRef.current) {
            questionVideoRef.current.pause();
        }
        setIsQuestionVideoPlaying(false);
        setCountdown(3);
        setStep("question"); // Stay in question step, show countdown on left
        let count = 3;
        const interval = setInterval(() => {
            count -= 1;
            setCountdown(count);
            if (count === 0) {
                clearInterval(interval);
                setCountdown(0);
                beginRecording();
            }
        }, 1000);
    };

    const beginRecording = async () => {
        setStep("recording");
        setRecording(true);
        setTimer(0);
        chunksRef.current = [];
        try {
            if (questionVideoRef.current) {
                questionVideoRef.current.pause();
            }
            if (!stream) {
                console.error("No media stream available for recording.");
                return;
            }
            mediaRecorderRef.current = new window.MediaRecorder(stream, {
                mimeType: 'video/webm;codecs=vp8,opus',
                videoBitsPerSecond: 1000000
            });
            mediaRecorderRef.current.ondataavailable = (e) => {
                if (e.data.size > 0) chunksRef.current.push(e.data);
            };
            mediaRecorderRef.current.onstop = () => {
                const recordedBlob = new Blob(chunksRef.current, { type: "video/webm" });
                setRecordedBlob(recordedBlob);
                setRecordedUrl(URL.createObjectURL(recordedBlob))
                setRecording(false);
                setStep("review");
            };
            mediaRecorderRef.current.start();
            let t = 0;
            const recInterval = setInterval(() => {
                t += 1;
                setTimer(t);
                if (t >= MAX_RECORD_TIME) {
                    stopRecording();
                }
            }, 1000);
            mediaRecorderRef.current.recInterval = recInterval;
        } catch (err) {
            console.error("Error accessing media devices.", err);
            setStep("question");
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
            clearInterval(mediaRecorderRef.current.recInterval);
            mediaRecorderRef.current.stop();
        }
    };
    const handleSubmit = async () => {
        setLoading(true);
        setUploadPercent(0);

        try {
            if (!recordedBlob) {
                console.error("No recorded blob to submit.");
                return;
            }

            const formData = new FormData();
            formData.append('question_id', currentQuestion?.question_id);
            formData.append('file', recordedBlob, `video-answer-${currentQuestion?.question_id}.webm`);

            const res = await APIServices.post({
                url: `/video-assessment/upload-answer`,
                payload: formData,
                config: {
                    headers: {
                        "Content-Type": "multipart/form-data"
                    },
                    onUploadProgress: (progressEvent) => {
                        const percent = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                        setUploadPercent(percent);
                    }
                }
            });

            if (res && res.status === 200) {
                setUploadPercent(100);

                if (currentQuestionIndex < totalQuestions - 1) {
                    setCurrentQuestionIndex(currentQuestionIndex + 1);
                    setRecordedBlob(null);
                    setStep("question");
                } else {
                    setStep("finished");
                }
            }

        } finally {
            setLoading(false);
            setIsQuestionVideoPlaying(true);
            setAttemptCount(0);
        }
    };


    // const handleSubmit = async () => {
    //     setLoading(true);
    //     try {

    //         if (!recordedBlob) {
    //             console.error("No recorded blob to submit.");
    //             return;
    //         }

    //         const formData = new FormData();
    //         formData.append('question_id', currentQuestion?.question_id);
    //         formData.append('file', recordedBlob, `video-answer-${currentQuestion?.question_id}.webm`);

    //         const res = await APIServices.post({
    //             url: `/video-assessment/upload-answer`,
    //             payload: formData,
    //         });
    //         if (res && res.status === 200) {
    //             if (currentQuestionIndex < totalQuestions - 1) {
    //                 setCurrentQuestionIndex(currentQuestionIndex + 1);
    //                 setRecordedBlob(null);
    //                 setStep("question");
    //             } else {
    //                 setStep("finished");
    //             }
    //         } else {
    //             console.error("Upload failed:", res);
    //         }
    //     } finally {
    //         setLoading(false);
    //         setIsQuestionVideoPlaying(true)
    //         setAttemptCount(0);
    //     }
    // };

    const handleRetake = () => {
        setAttemptCount(attemptCount + 1);
        setRecordedBlob(null);
        startRecording();
    };

    const formatTime = (seconds) => {
        return `${Math.floor(seconds / 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;
    }

    const handleVisibilityChange = async () => {
        if (document.hidden) {
            setIsTabActive(false);
            setSwitchCount((prev) => prev + 1);
            setOpenWarning(true);

            const response = await APIServices.post({
                url: `/switch-tabs`,
                payload: { "section_id": 4 },
            });

            if (response && response.status === 201) {
                console.log("User switched tab!");
            } else {
                console.error("Failed to log:", response);
            }

            // console.log(" User switched tab!");
        } else {
            setIsTabActive(true);
            // console.log("User returned to exam tab.");
        }
    };

    const handleClose = (event, reason) => {
        if (reason === "clickaway") return;
        setOpenWarning(false);
    };


    const renderContent = () => {
        if (step === "start") {
            return (
                <Suspense>
                    <MediaErrorModal visible={!!mediaError} message={mediaError} onClose={() => setMediaError(null)} />
                    <PreviewCamRec visible={showPreviewModal} onClose={() => setShowPreviewModal(false)} />
                    <InterviewInstructions
                        onStart={() => setStep("question")}
                        onPreview={() => setShowPreviewModal(true)}
                        mediaError={mediaError}
                    />
                </Suspense>
            );
        }

        if (questions.length === 0) {
            return (
                <div className="min-h-screen flex flex-col items-center justify-center bg-slate-100">
                    <Typography variant="h5">Loading questions...</Typography>
                    <span>Please refresh the page</span>
                </div>
            );
        }

        if (step === "finished") {
            return (
                <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
                    <Paper elevation={3} className="p-12  items-center">
                        <Typography variant="h4" color="success.main" className="font-bold mb-4">
                            Interview Complete!
                        </Typography>
                        <Typography variant="h6" className="text-center">
                            Thank you for your responses.
                        </Typography>
                    </Paper>
                </div>
            );
        }

        return (
            <div className=" flex flex-col w-full h-fit min-h-screen gap-4 items-center justify-start bg-slate-50 px-8 ">
                <Suspense fallback={<div></div>}>
                    <WarningNotif
                        open={openWarning}
                        onClose={handleClose}
                        switchCount={switchCount}
                    />
                </Suspense>
                <div className="flex flex-row w-full justify-between items-start mt-2 ">
                    <div className='self-start p-2 '>
                        <img className="default_logo lazyloaded " src="https://www.sixelevenbpo.com/wp-content/uploads/2020/05/six-eleven-center-logo.png" alt="six-eleven-center-logo" data-ll-status="loaded" />
                    </div>
                    <h1 className='text-3xl font-bold p-1 '>One-way Interview</h1>

                </div>
                <div className="flex flex-col w-full ">
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_8fr_1fr] gap-4 items-stretch">
                        <Paper elevation={3} className="p-3 flex items-center justify-center">
                            <Typography variant="h6" className="font-semibold">
                                Question {currentQuestionIndex + 1} / {totalQuestions}
                            </Typography>
                        </Paper>
                        <Paper elevation={3} className="p-3 flex items-center gap-4">{
                            (() => {
                                let progressPercent = totalQuestions > 0 ? ((currentQuestionIndex + 1) / totalQuestions) * 100 : 0;
                                if (progressPercent > 90) progressPercent = 98;
                                return (<>
                                    <div className="w-full my-2 md:my-4 bg-gray-200 rounded-full h-2 md:h-3">
                                        <div
                                            className="bg-green-700 h-2 md:h-3 rounded-full"
                                            style={{
                                                width: `${progressPercent}%`,
                                            }}
                                        ></div>
                                    </div>
                                    <Typography variant="h6" className="font-semibold ">
                                        {`${Math.round(progressPercent)}%`}
                                    </Typography>
                                </>);
                            })()
                        }</Paper>
                        <div
                            className={`${(timeLeft ?? 0) > 30 ? "bg-white" : "text-red-600 bg-red-50"
                                } flex flex-row border shadow-md p-2 md:p-3 rounded-md text-base md:text-lg overflow-auto items-center justify-evenly gap-2`}
                        >
                            <BsClock size={20} className="md:size-[25px]" />
                            <div className="flex flex-col items-end">
                                <span
                                    className={`text-[18px] md:text-[25px] ${(timeLeft ?? 0) > 30 ? "text-green-700" : "text-red-600"
                                        }`}
                                >
                                    <span className="font-bold">{formatTime(timeLeft ?? 0)}</span>
                                </span>
                                <span className="text-xs md:text-sm italic text-black">
                                    Time Left
                                </span>
                            </div>
                        </div>


                    </div>
                </div>
                {isloadingQuestions ? <div className="flex flex-col items-center justify-center self-center gap-3 ">
                    <CircularProgress />
                    <span className="font-medium">Loading questions...</span>
                </div> : <div className="w-full max-w-8xl flex flex-col bg-white rounded-md shadow-md p-8  ">
                    <p className='text-red-500 text-2xl pb-4'><span className='font-bold'>Instructions: </span>{"Watch the video, keep eye contact with the camera, then click Start Recording"}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                        {/* Question Video Panel */}
                        <div className="relative max-h-[60vh]">
                            <video
                                ref={questionVideoRef}
                                src={`${storageApi}videos/${currentQuestion?.videos[0]?.file_url}`}
                                className="w-full h-full rounded-lg shadow bg-black"
                                autoPlay
                                onPlay={() => setIsQuestionVideoPlaying(true)}
                                onPause={() => setIsQuestionVideoPlaying(false)}
                                onTimeUpdate={handleTimeUpdate}
                                muted={isQuestionVideoMuted}
                                playsInline
                                controls={false}
                            />
                            <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/70 to-transparent text-white rounded-b-lg flex flex-col items-center justify-end">
                                <Typography variant="h6" className="mb-2 text-center text-black bg-white">{currentQuestion?.question_text}</Typography>
                                <div className="w-full flex justify-between items-center gap-2">
                                    <Button onClick={handleVideoPlayPause} size="small" sx={{ color: 'white', minWidth: '32px', padding: '4px' }}>
                                        {isQuestionVideoPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                                    </Button>
                                    <LinearProgress
                                        variant="determinate"
                                        value={questionVideoProgress}
                                        className="flex-grow mx-2"
                                        sx={{ height: 4, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.3)', '& .MuiLinearProgress-bar': { backgroundColor: 'white' } }}
                                    />
                                    <Button onClick={handleQualityClick} size="small" sx={{ color: 'white', minWidth: '32px', padding: '4px' }}>
                                        {videoQuality !== null ? `${videoQuality}p` : 'HD'}
                                    </Button>
                                    <Menu
                                        anchorEl={anchorElQuality}
                                        open={openQualityMenu}
                                        onClose={handleQualityClose}
                                    >
                                        <MenuItem onClick={() => handleQualitySelect('360')}>360p</MenuItem>
                                        <MenuItem onClick={() => handleQualitySelect('480')}>480p</MenuItem>
                                        <MenuItem onClick={() => handleQualitySelect(null)}>HD</MenuItem>

                                    </Menu>
                                    <Button onClick={handleVolumeToggle} size="small" sx={{ color: 'white', minWidth: '32px', padding: '4px' }}>
                                        <VolumeUpIcon />
                                    </Button>
                                    <Button onClick={handleFullscreen} size="small" sx={{ color: 'white', minWidth: '32px', padding: '4px' }}>
                                        <FullscreenIcon />
                                    </Button>
                                </div>
                            </div>
                        </div>
                        {/* User's Video Panel */}
                        <div className="relative max-h-[60vh] flex  bg-black rounded-lg shadow">
                            <video
                                key={step === 'review' && recordedBlob ? 'review-video' : 'live-video'}
                                ref={step !== 'review' || !recordedBlob ? webcamRef : null}
                                src={step === 'review' && recordedBlob ? recordedURL : undefined}
                                controls={step === 'review' && !!recordedBlob}
                                autoPlay
                                muted={step !== 'review'}
                                onEnded={(e) => {
                                    if (step === "review") {
                                        e.currentTarget.pause();
                                        e.currentTarget.currentTime = e.currentTarget.duration; // stay at end
                                    }
                                }}
                                className="w-full rounded-lg shadow bg-black"
                            />
                            {step === "recording" && (
                                <div className="absolute top-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full flex items-center">
                                    <FiberManualRecordIcon fontSize="small" color="error" />
                                    <Typography variant="caption" className="ml-1 font-mono">REC {formatTime(timer)}</Typography>
                                </div>
                            )}
                            {step === "recording" && (
                                <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
                                    <Button
                                        variant="contained"
                                        color="error"
                                        size="large"
                                        startIcon={<StopIcon />}
                                        onClick={stopRecording}
                                    >
                                        Stop Recording
                                    </Button>
                                </div>
                            )}
                            {/* Countdown badge on left */}
                            {countdown > 0 && (
                                <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
                                    <div className="bg-black/70 text-white px-8 py-6 rounded-full flex flex-col items-center">
                                        <Typography variant="h2" className="font-bold">{countdown}</Typography>
                                        <Typography variant="body1" className="mt-2">Get ready...</Typography>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    {/* {isLoading && <p>Uploading: {uploadPercent}%</p>} */}


                    {/* Action Buttons */}
                    <div className="flex justify-center mt-6 gap-2">
                        {step === "question" && countdown === 0 && (
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={startRecording}
                                disabled={isQuestionVideoPlaying === true}
                            >
                                {isQuestionVideoPlaying ? "Listening to the Question..." : "Start Recording"}
                            </Button>
                        )}
                        {step === "review" && (
                            <>
                                {attemptCount === 3 ? <span className="text-gray-500 self-center mr-4">
                                    {"Retake Disabled (Max Attempts Reached)"}
                                </span> : <Button
                                    variant="outlined"
                                    color="primary"
                                    size="large"
                                    disabled={isLoading || attemptCount === 3}
                                    onClick={handleRetake}

                                >
                                    Retake {attemptCount > 0 ? `(${attemptCount}/3)` : ""}
                                </Button>}
                                <Button
                                    variant="contained"
                                    color={"primary"}
                                    size="large"
                                    disabled={isLoading}
                                    onClick={handleSubmit}
                                    className="mr-4"
                                >
                                    {isLoading ? "Submitting..." : "Submit Answer"}
                                </Button>

                            </>
                        )}

                    </div>
                </div>}
            </div>
        );
    };

    return <>{renderContent()}</>;
};

export default OnewayInterview;
